import { Box, Button, Tooltip } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { isLastVisible } from '../../library/seriesVisibility';
import ResourcesIds from '../../resources/ResourcesIds';
import {
    legendButtonStyles,
    legendItemStyles,
    legendListStyles,
    legendSwatchStyles,
} from './chartLegend.styles';

export interface ChartLegendItem {
    id: string;
    label: string;
    color: string;
}

interface ChartLegendProps {
    items: ChartLegendItem[];
    hidden: ReadonlySet<string>;
    onToggle: (id: string) => void;
    /** Names the group for a screen reader, e.g. "Series shown". */
    groupLabel: string;
}

/**
 * The chart's legend, and its series switch.
 *
 * Ours rather than the library's because hiding a series is implemented by removing it
 * from the chart -- at which point the built-in legend, which is derived from whatever is
 * drawn, loses that entry and the reader has no way to bring it back.
 *
 * The last visible series cannot be hidden. Its button stays enabled and focusable and is
 * marked `aria-disabled` instead: a `disabled` button drops out of the tab order and stops
 * announcing itself, which is the wrong answer for a control that exists but declines to
 * act right now.
 */
export const ChartLegend = ({ items, hidden, onToggle, groupLabel }: ChartLegendProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const allIds: string[] = items.map(item => item.id);

    return (
        <Box component="ul" sx={legendListStyles} aria-label={groupLabel}>
            {items.map(item => {
                const isHidden: boolean = hidden.has(item.id);
                const isLocked: boolean = isLastVisible(hidden, item.id, allIds);

                const button = (
                    <Button
                        size="small"
                        sx={legendButtonStyles(isHidden)}
                        // A toggle button rather than a checkbox: it switches a thing on
                        // and off rather than selecting it from a set.
                        aria-pressed={!isHidden}
                        aria-disabled={isLocked || undefined}
                        // Says what the click will do, not what the state is -- the
                        // pressed state already carries that.
                        aria-label={translate(
                            isHidden ? ResourcesIds.DASHBOARD_LEGEND_SHOW : ResourcesIds.DASHBOARD_LEGEND_HIDE,
                            { series: item.label }
                        )}
                        onClick={() => onToggle(item.id)}
                    >
                        <Box component="span" sx={legendSwatchStyles(item.color, isHidden)} />
                        {item.label}
                    </Button>
                );

                return (
                    <Box component="li" key={item.id} sx={legendItemStyles}>
                        {isLocked ? (
                            <Tooltip
                                describeChild
                                enterTouchDelay={0}
                                title={translate(ResourcesIds.DASHBOARD_LEGEND_LAST_SERIES)}
                            >
                                {button}
                            </Tooltip>
                        ) : (
                            button
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};
